import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Subject } from 'rxjs';

const ACTIVITY_WRITE_THROTTLE_MS = 1000;

@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly idle = inject(Idle);
  private readonly sessionExpiredSubject = new Subject<void>();
  readonly sessionExpired$ = this.sessionExpiredSubject.asObservable();
  private lastWriteTime = 0;

  constructor() {
    this.idle.setIdle(1800);
    this.idle.setTimeout(0);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onIdleStart
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handleIdleStart());

    this.destroyRef.onDestroy(() => this.removeActivityListeners());
  }

  /** Starts or stops idle tracking and its browser activity listeners. */
  setLoggedIn(isLoggedIn: boolean): void {
    if (isLoggedIn) {
      this.updateLastActiveTime();
      this.idle.watch();
      this.setupActivityListeners();
      return;
    }

    this.idle.stop();
    this.removeActivityListeners();
  }

  /** Handles idle events while respecting activity in other browser tabs. */
  private handleIdleStart(): void {
    const lastActive = Number.parseInt(
      localStorage.getItem('lastActiveTime') || '0',
      10
    );
    const timeSinceLastActive = Date.now() - lastActive;

    if (timeSinceLastActive >= 1800000) {
      this.sessionExpiredSubject.next();
    } else {
      this.idle.watch();
    }
  }

  /** Stores activity timestamps for other tabs and subsequent idle checks. */
  private readonly updateLastActiveTime = (): void => {
    const now = Date.now();
    // Capture-phase scroll/click/keydown fire constantly; one write per second is enough.
    if (now - this.lastWriteTime < ACTIVITY_WRITE_THROTTLE_MS) {
      return;
    }
    this.lastWriteTime = now;
    localStorage.setItem('lastActiveTime', now.toString());
  };

  /** Resets idle tracking when another tab reports recent activity. */
  private readonly handleStorageChange = (event: StorageEvent): void => {
    if (event.key === 'lastActiveTime') {
      this.idle.watch();
    }
  };

  /** Registers the browser activity listeners used by session tracking. */
  private setupActivityListeners(): void {
    window.addEventListener('storage', this.handleStorageChange);
    document.addEventListener('click', this.updateLastActiveTime, true);
    document.addEventListener('keydown', this.updateLastActiveTime, true);
    document.addEventListener('scroll', this.updateLastActiveTime, true);
  }

  /** Removes session activity listeners after logout or service destruction. */
  private removeActivityListeners(): void {
    window.removeEventListener('storage', this.handleStorageChange);
    document.removeEventListener('click', this.updateLastActiveTime, true);
    document.removeEventListener('keydown', this.updateLastActiveTime, true);
    document.removeEventListener('scroll', this.updateLastActiveTime, true);
  }
}
